'use client';

import { useTranslation } from 'react-i18next';
import type { SupportedLanguage } from '@/i18n';
import { setStoredLanguage } from '@/i18n';

const LANGUAGE_OPTIONS: { code: SupportedLanguage; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'zh', label: '中文' },
];

interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'buttons';
  className?: string;
}

export default function LanguageSwitcher({
  variant = 'buttons',
  className = '',
}: LanguageSwitcherProps) {
  const { i18n } = useTranslation();

  const currentLang = (i18n.language?.split('-')[0] || 'en') as SupportedLanguage;
  const effectiveLang = ['en', 'zh'].includes(currentLang) ? currentLang : 'en';

  const setLanguage = (code: SupportedLanguage) => {
    setStoredLanguage(code);
    i18n.changeLanguage(code);
  };

  if (variant === 'dropdown') {
    return (
      <select
        value={effectiveLang}
        onChange={(e) => setLanguage(e.target.value as SupportedLanguage)}
        className={`bg-transparent border border-gray-300 dark:border-white/20 rounded-lg px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer ${className}`}
        aria-label="Select language"
      >
        {LANGUAGE_OPTIONS.map((opt) => (
          <option key={opt.code} value={opt.code}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {LANGUAGE_OPTIONS.map((opt) => (
        <button
          key={opt.code}
          onClick={() => setLanguage(opt.code)}
          className={`px-2.5 py-1 rounded-md text-sm font-medium transition-colors ${
            effectiveLang === opt.code
              ? 'bg-primary/20 text-primary'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5'
          }`}
          aria-label={`Switch to ${opt.label}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
